// Shopify-embedded mode.
//
// The partner portal runs in two places:
//
//   • Firebase Hosting — a signed-in Outfino user, token in localStorage,
//     talking to /v3/brands/*.
//   • Inside the Shopify admin iframe, served from the API at
//     /v3/shopify/app — no Outfino account exists at all. The only credential
//     is an App Bridge session token, and the endpoints live under
//     /v3/shopify/admin/*, which resolve the brand from that token.
//
// Everything that differs between the two lives in this file. The components
// are identical in both modes; see api/docs/SHOPIFY_WEB_MANAGEMENT.md.

/// True when App Bridge put its global on the page — i.e. we are inside the
/// Shopify admin iframe. The build also sets REACT_APP_EMBEDDED, but the
/// runtime check is what actually decides, so a misbuilt bundle degrades to
/// the normal portal rather than to a blank screen.
export const isEmbedded = () =>
  typeof window !== 'undefined' && typeof window.shopify?.idToken === 'function';

/// A fresh App Bridge session token. They live about a minute, so we mint one
/// per request rather than caching — which also means the session-token
/// handshake Shopify's embedded-app checks look for happens continuously.
export async function sessionToken() {
  return window.shopify.idToken();
}

// ---------------------------------------------------------------------------
// Path mapping
//
// The embedded endpoints are brand-scoped by the session token, so the brand id
// in the portal's URLs is dropped. Anything with no embedded equivalent returns
// null and the caller turns that into an UnsupportedInEmbedded error. Paths in
// PASS_THROUGH / BRAND_PASS_THROUGH keep their portal URL (see below).
// ---------------------------------------------------------------------------

// The brand id segment. Split the query string off FIRST: `[^/]+` happily eats
// `b-1?fetch_live_markets=false` in one bite, which silently dropped the query
// and made every embedded brand load trigger a live Shopify markets fetch.
const BRAND = /^\/brands\/[^/?#]+/;

const PASS_THROUGH = [/^\/drops\/brand\/[^/]+$/, /^\/users\/search$/];
// Galaxy gift drops (/brands/:id/gift-drops*) and prize wheels
// (/brands/:id/prize-wheels*) take the Shopify session token like the
// campaigns do (requireAuthOrShopify + manageCampaigns).
// /building: the Profile's 3D store preview (custom brand building, if any).
// The API guards it with requireAuthOrShopify, so the session token works.
// The fit-check embed insights (/brands/:id/embed/*) likewise.
const BRAND_PASS_THROUGH = [/^\/campaigns(\/.*)?$/, /^\/orders$/, /^\/discounts(\/.*)?$/, /^\/gift-drops(\/.*)?$/, /^\/prize-wheels(\/.*)?$/, /^\/building$/, /^\/embed(\/.*)?$/];

export function mapPath(rawPath, method = 'GET') {
  // Already an embedded path (callers that have no portal equivalent, e.g.
  // per-product visibility) — pass through untouched.
  if (rawPath.startsWith('/shopify/admin/')) return rawPath;

  const qIndex = rawPath.search(/[?#]/);
  const path = qIndex === -1 ? rawPath : rawPath.slice(0, qIndex);
  const query = qIndex === -1 ? '' : rawPath.slice(qIndex);

  // Session bootstrap — there is no Outfino user to fetch.
  if (path === '/user/me') return '/shopify/admin/session';

  if (PASS_THROUGH.some((re) => re.test(path))) return rawPath;

  // Shipping (#623 B4). These are brand-scoped by query/body, not /brands/:id
  // paths, so they never match BRAND below — handle them explicitly. The
  // embedded pair drops the brand_id (the session token supplies the brand) and
  // hits the /shopify/admin/shipping/* routes.
  //   • GET  /shipping/coverage?brand_id=<id>  → /shopify/admin/shipping/coverage
  //   • POST /shipping/rates                   → /shopify/admin/shipping/rates
  //   (The former B5 brand-owned rate CRUD, /shipping/rates/brand, is no longer
  //   called by the portal — Shopify is the only source of shipping fees since
  //   the 2026-09-22 decision — so it has no mapping here and would fail
  //   loudly, like any other unsupported path.)
  if (path === '/shipping/coverage') return '/shopify/admin/shipping/coverage';
  if (path === '/shipping/rates') return '/shopify/admin/shipping/rates';

  if (!BRAND.test(path)) return null;         // /drops/*, /users/search, /auth …

  const rest = path.replace(BRAND, '');       // '' | '/products' | '/stats/overview'

  if (BRAND_PASS_THROUGH.some((re) => re.test(rest))) return rawPath;
  // The community challenge calendar takes the session token as well
  // (requireAuthOrShopify + viewProducts).
  if (rest === '/challenge-calendar') return rawPath;
  // The Prize Pool likewise (requireAuthOrShopify + viewProducts / manageCampaigns).
  if (rest === '/prize-pool' || rest.startsWith('/prize-pool/')) return rawPath;
  if (rest.startsWith('/members') && method !== 'GET') return rawPath;
  if (rest === '' && method === 'DELETE') return rawPath;

  // GET/PUT /brands/:id  →  the brand itself.
  if (rest === '') return `/shopify/admin/brand${query}`;

  // documents#188 — per-product relabel. Checked BEFORE the list rule below,
  // which would otherwise swallow every /products* path into the list call.
  //   POST /brands/:id/products/:pid/relabel → /shopify/admin/products/:pid/relabel
  const relabel = rest.match(/^\/products\/([^/]+)\/relabel$/);
  if (relabel) return `/shopify/admin/products/${relabel[1]}/relabel`;

  const dispute = rest.match(/^\/products\/([^/]+)\/label-disputes$/);
  if (dispute) return `/shopify/admin/products/${dispute[1]}/label-disputes`;
  if (rest === '/label-disputes') return '/shopify/admin/label-disputes';

  // The embedded products endpoint pages (it has to — it returns hidden
  // products too, which getBrandProducts filters out). The portal's ProductsTab
  // shows the whole catalogue in one grid, so ask for a full page.
  if (rest === '/sync-progress') return '/shopify/admin/products/sync-progress';
  if (rest.startsWith('/products')) return '/shopify/admin/products?limit=250';
  if (rest === '/sync-products') return '/shopify/admin/products/sync';
  if (rest.startsWith('/members')) return '/shopify/admin/members';
  if (rest.startsWith('/stats/')) return `/shopify/admin${rest}${query}`;

  return null;                                // /campaigns, /test-shopify-connection …
}

/// Thrown when a component reaches for something the embedded surface does not
/// expose. Callers should be gating on `isEmbedded()` before that happens; this
/// is the backstop that keeps a stray call from looking like a network error.
export class UnsupportedInEmbedded extends Error {
  constructor(path) {
    super(`Not available in the Shopify embedded app: ${path}`);
    this.name = 'UnsupportedInEmbedded';
    this.status = 501;
    this.path = path;
  }
}

// ---------------------------------------------------------------------------
// Tab availability
//
// Every portal tab is offered inside the Shopify admin. The ones with no
// /v3/shopify/admin endpoint (drops, campaigns, orders, member changes, brand
// deletion) call the regular portal endpoints with the Shopify session token;
// the API's requireAuthOrShopify accepts it there, scoped to this shop's brand
// and acting as its Outfino owner.
//
// `challenges` (galaxy gift drops + prize wheels) calls /brands/:id/gift-drops*
// and /brands/:id/prize-wheels* the same way; only its live socket is portal-only (the socket refuses a session token), so
// inside the Shopify admin that page polls.
// ---------------------------------------------------------------------------

// Not offered here: `followers` and `billing`. Their endpoints take no Shopify
// session token (no mapping below), and an app inside the Shopify admin has
// to charge through Shopify's own billing, not a card form of ours.
export const EMBEDDED_TABS = new Set(['profile', 'products', 'drops', 'discounts', 'campaigns', 'challenges', 'calendar', 'prizePool', 'stats', 'orders', 'payouts', 'members', 'special', 'shipping', 'embedded-stats', 'embedded-visitors', 'embedded-settings']);

export const tabAvailable = (key) => !isEmbedded() || EMBEDDED_TABS.has(key);

// ---------------------------------------------------------------------------
// Returning to the partner portal
//
// When the merchant reached Shopify from the portal's "add a brand", the API
// hands the return URL back on the session response — see
// controllers/shopify_admin_controller.js and auth/AuthContext.js. It arrives
// exactly once, and only for a session that started in the portal; a merchant
// who installed straight from Shopify never gets one and stays here, which is
// the surface App Review looks at under 1.1.12.
//
// A plain navigation would move the iframe, not the top window, so the bounce
// has to go through App Bridge.
// ---------------------------------------------------------------------------

/// Move the merchant's actual browser window, from inside the Shopify admin
/// iframe.
///
/// App Bridge patches window.open: a `_top` target becomes a
/// Navigation.redirect.remote message to the admin, which navigates the real
/// window. That is a navigation, not a popup, so it survives being called on
/// load rather than from a click.
///
/// This used to call `shopify.redirectTo(url, { newContext: true })`. No such
/// method exists — App Bridge has never shipped one — so the call was skipped
/// and control fell through to a `_blank` window.open, which the browser then
/// blocked as an unrequested popup. The automatic bounce back to the portal
/// silently did nothing; only the manual button worked, because a click is a
/// user gesture.
export function openTopLevel(url) {
  try {
    window.open(url, '_top');
    return;
  } catch (e) {
    // No App Bridge (the portal opened outside the Shopify admin) — a plain
    // navigation is then the right thing anyway.
  }
  window.location.assign(url);
}
